import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IApplicationUser, defaultValue } from 'app/shared/model/application-user.model';
import { getSession } from 'app/shared/reducers/authentication';

const initialState: EntityState<IApplicationUser> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/application-users';
const apiUrlViews = 'api/application-users/views';

// Actions

export const getEntities = createAsyncThunk('applicationUser/fetch_entity_list', async ({ page, size, sort, filterUrl }: IQueryParams) => {
  const requestUrl = `${apiUrl}?${filterUrl ? filterUrl + '&' : ''}cacheBuster=${new Date().getTime()}`;
  return axios.get<IApplicationUser[]>(requestUrl);
});

export const getEntitiesView = createAsyncThunk(
  'applicationUser/fetch_entity_list',
  async ({ page, size, sort, filterUrl }: IQueryParams) => {
    const requestUrl = `${apiUrlViews}?${filterUrl ?? ''}&cacheBuster=${new Date().getTime()}`;
    return axios.get<IApplicationUser[]>(requestUrl);
  },
);

export const getEntity = createAsyncThunk(
  'applicationUser/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IApplicationUser>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'applicationUser/create_entity',
  async (entity: IApplicationUser, thunkAPI) => {
    const result = await axios.post<IApplicationUser>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'applicationUser/update_entity',
  async (entity: IApplicationUser, thunkAPI) => {
    const result = await axios.put<IApplicationUser>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateApplicationUser = createAsyncThunk(
  'applicationUser/update_user',
  async (entity: IApplicationUser, thunkAPI) => {
    const result = await axios.put<IApplicationUser>(`${apiUrl}/custom/${entity?.appUserId}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateCurrentUser = createAsyncThunk(
  'applicationUser/update_user',
  async (entity: IApplicationUser, thunkAPI) => {
    const result = await axios.put<IApplicationUser>(`${apiUrl}/custom/${entity?.appUserId}`, cleanEntity(entity));
    thunkAPI.dispatch(getSession());
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const inviteApplicationUser = createAsyncThunk(
  'applicationUser/invite_user',
  async (entity: IApplicationUser, thunkAPI) => {
    const result = await axios.post<IApplicationUser>(`api/invite`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'applicationUser/partial_update_entity',
  async (entity: IApplicationUser, thunkAPI) => {
    const result = await axios.patch<IApplicationUser>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'applicationUser/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IApplicationUser>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const ApplicationUserSlice = createEntitySlice({
  name: 'applicationUser',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(inviteApplicationUser.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesView), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, updateApplicationUser, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntitiesView), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, updateApplicationUser, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = ApplicationUserSlice.actions;

// Reducer
export default ApplicationUserSlice.reducer;
